import { useEffect, useRef, useState } from 'react';
import { everyWhileVisible } from '../../lib/visiblePolling';
import {
  Avatar, Box, CircularProgress, Drawer, IconButton, List, ListItemButton,
  ListItemIcon, ListItemText, Stack, Typography, Divider, useMediaQuery, useTheme,
  Menu as MuiMenu, MenuItem, Chip, Tooltip, alpha, Badge, ButtonBase, ListItem,
} from '@mui/material';
import { Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
  LayoutDashboard, Calendar, CalendarPlus, HeartPulse, Receipt, Heart,
  Package, User, LogOut, X, Building2, Check, MessageCircle,
  Users, Menu, ArrowLeftRight, Truck, ShoppingBag, ShieldCheck, HelpCircle,
  ChevronDown, Sparkles, ClipboardCheck, FileSignature, Glasses, Smile, MessagesSquare,
} from 'lucide-react';
import { usePatientAuth } from '../../contexts/PatientAuthContext';
import { usePatientTenant } from '../../contexts/PatientTenantContext';
import { usePatientDependents } from '../../contexts/PatientDependentContext';
import { design } from '../../theme/clinveaDesign';
import { usePendingCheckins } from './PendingCheckinsCard';
import { usePatientFormsWaitingState, usePatientNewFormAlert } from '../consentForms/PatientFormsWaitingCard';
import { PatientTermsAndConditions } from './PatientTermsAndConditions';
import { PendingDeletionDialog } from '../account/PendingDeletionDialog';
import { patientApi, fetchPatientApi } from '../../lib/api';
import { extractData } from '../../hooks/useQueries';
import toast from 'react-hot-toast';
import { useTranslation, Trans } from 'react-i18next';
import { enumLabel } from '../../i18n/enumLabel';
import i18n from '../../i18n';

const SIDEBAR_WIDTH = 248;
/** Avatar fill in the top bar and account menu. The sidebar itself uses the staff shell's navy tokens. */
const PORTAL_HEADER_GRADIENT = 'linear-gradient(135deg, #0d2851 0%, #1149a0 55%, #1565c0 100%)';
const SIDEBAR_WIDTH_COLLAPSED = 76;
const SIDEBAR_COLLAPSED_KEY = 'patient_sidebar_collapsed';

interface NavItem {
  /** portal-namespace key, resolved at render time */
  labelKey: string;
  path: string;
  icon: typeof LayoutDashboard;
  badgeKey?: 'messages' | 'checkins' | 'forms';
  /** Other routes that should highlight this item (pages folded into it, deep links). */
  alsoActiveOn?: string[];
  /** Hidden unless the patient actually has something there. */
  onlyIf?: 'hasPlans' | 'homeCollection' | 'medicineOrders' | 'insurance';
}
interface NavGroup { titleKey: string; items: NavItem[] }

// Consolidated from 14 items to 8 (+ My Plans when enrolled). Nothing was removed: Vitals,
// Prescriptions, Lab Results, My Records and Referrals are tabs of Health Records; Find Care is
// reached from Book Appointment; Data Sharing lives in Profile. Their old URLs still work.
const NAV_GROUPS: NavGroup[] = [
  { titleKey: 'navGroupOverview', items: [
    { labelKey: 'navHome', path: '/patient/dashboard', icon: LayoutDashboard },
  ] },
  { titleKey: 'appointments', items: [
    { labelKey: 'bookAppointment', path: '/patient/appointments/book', icon: CalendarPlus, alsoActiveOn: ['/patient/find-care'] },
    { labelKey: 'myAppointments', path: '/patient/appointments', icon: Calendar, badgeKey: 'checkins' },
    { labelKey: 'sdTitle', path: '/patient/saved-doctors', icon: Heart },
    { labelKey: 'askADoctor', path: '/patient/questions', icon: HelpCircle },
    { labelKey: 'messages', path: '/patient/messages', icon: MessageCircle, badgeKey: 'messages' },
  ] },
  { titleKey: 'navGroupHealth', items: [
    { labelKey: 'healthRecords', path: '/patient/health-records', icon: HeartPulse,
      alsoActiveOn: ['/patient/vitals', '/patient/prescriptions', '/patient/lab-results', '/patient/records', '/patient/referrals'] },
    { labelKey: 'aiHealthSummary', path: '/patient/ai-summary', icon: Sparkles },
    { labelKey: 'navAskClinVea', path: '/patient/assistant', icon: MessagesSquare },
    { labelKey: 'navTreatmentPlans', path: '/patient/treatment-plans', icon: ClipboardCheck },
    { labelKey: 'navEyePrescriptions', path: '/patient/eye-prescriptions', icon: Glasses },
    { labelKey: 'navGlassesOrders', path: '/patient/glasses-orders', icon: ShoppingBag },
    { labelKey: 'navMyTeeth', path: '/patient/my-teeth', icon: Smile },
    // Procedure consent forms the clinic sent to read and sign (A4); the count is what still waits.
    { labelKey: 'navFormsToSign', path: '/patient/forms-to-sign', icon: FileSignature, badgeKey: 'forms' },
    { labelKey: 'navHomeSampleCollection', path: '/patient/home-collection', icon: Truck, onlyIf: 'homeCollection' },
    { labelKey: 'navOrderMedicines', path: '/patient/medicine-orders', icon: ShoppingBag, onlyIf: 'medicineOrders' },
  ] },
  { titleKey: 'navGroupAccount', items: [
    { labelKey: 'myBills', path: '/patient/bills', icon: Receipt },
    { labelKey: 'navMyInsurance', path: '/patient/insurance', icon: ShieldCheck, onlyIf: 'insurance' },
    { labelKey: 'navMyPlans', path: '/patient/plans', icon: Package, onlyIf: 'hasPlans' },
    { labelKey: 'family', path: '/patient/family', icon: Users },
    { labelKey: 'profile', path: '/patient/profile', icon: User, alsoActiveOn: ['/patient/consents'] },
  ] },
];

const UNREAD_POLL_MS = 25_000;

function initials(name?: string) {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('');
}

/**
 * Persistent shell for the whole patient portal — mounted once by the pathless
 * <PatientPortalRoute> parent route so it survives navigation between pages.
 *
 * Nav is a grouped left sidebar (desktop) / drawer (mobile) rather than a single flat row —
 * flat top-nav stopped scaling once Messages/Find Care were added (11 items wrapping awkwardly).
 */
export function PatientPortalLayout() {
  // Bound as `tx`: this file already uses `t` for something else.
  const { t: tx } = useTranslation('portal');
  const navigate = useNavigate();
  const location = useLocation();
  const { patient, loading, signOut, refresh } = usePatientAuth();
  const { linkedClinics, activeClinic, isMultiClinic, switchClinic } = usePatientTenant();
  const { dependents, activeDependent, switchToDependent, switchToSelf } = usePatientDependents();
  const activeDependents = dependents.filter((d) => d.status === 'ACTIVE');
  const theme = useTheme();
  // Same navy sidebar tokens as the staff/doctor/lab/pharmacy shell (components/layout/Layout.tsx).
  const nd = design(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [clinicMenuAnchor, setClinicMenuAnchor] = useState<null | HTMLElement>(null);
  const [familyMenuAnchor, setFamilyMenuAnchor] = useState<null | HTMLElement>(null);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<null | HTMLElement>(null);
  // Check-in forms waiting for the patient: a count on My Appointments (medopd-docs 140).
  const { data: pendingCheckins = [] } = usePendingCheckins();
  // Forms the clinic sent to sign: a count on Forms to sign, and a toast when a new one arrives (the app gets a push).
  const { count: formsWaiting, loaded: formsLoaded } = usePatientFormsWaitingState();
  usePatientNewFormAlert(formsWaiting, formsLoaded);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1');

  // Below md the sidebar is always the icon rail regardless of the saved preference — an expanded
  // sidebar would cover a phone screen. `collapsed` stays the user's desktop preference.
  const railMode = isMobile || collapsed;

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      return next;
    });
  };
  const prevUnreadRef = useRef<number | null>(null);

  // "My Plans" only appears for patients who are actually enrolled in a health package — for everyone
  // else it was an always-empty menu item. Re-checked when the active clinic changes. A failed check
  // keeps it hidden (the page itself stays reachable at /patient/plans).
  const [hasPlans, setHasPlans] = useState(false);
  useEffect(() => {
    if (!patient) return;
    let cancelled = false;
    fetchPatientApi<unknown[]>('/api/patient/health-packages/enrollments')
      .then((res) => { if (!cancelled) setHasPlans((extractData<unknown[]>(res) ?? []).length > 0); })
      .catch(() => { if (!cancelled) setHasPlans(false); });
    return () => { cancelled = true; };
  }, [patient, activeClinic?.clinicId]);

  // "Home Sample Collection" only when the active clinic offers it (plan module + clinic switched on).
  const [hasHomeCollection, setHasHomeCollection] = useState(false);
  useEffect(() => {
    if (!patient) return;
    let cancelled = false;
    fetchPatientApi<{ offered: boolean }>('/api/patient/home-collection/availability')
      .then((res) => { if (!cancelled) setHasHomeCollection(!!extractData<{ offered: boolean }>(res)?.offered); })
      .catch(() => { if (!cancelled) setHasHomeCollection(false); });
    return () => { cancelled = true; };
  }, [patient, activeClinic?.clinicId]);

  // "My Insurance" only when the active clinic manages insurance (plan module + switched on).
  const [hasInsurance, setHasInsurance] = useState(false);
  useEffect(() => {
    if (!patient) return;
    let cancelled = false;
    fetchPatientApi<{ offered: boolean }>('/api/patient/insurance/availability')
      .then((res) => { if (!cancelled) setHasInsurance(!!extractData<{ offered: boolean }>(res)?.offered); })
      .catch(() => { if (!cancelled) setHasInsurance(false); });
    return () => { cancelled = true; };
  }, [patient, activeClinic?.clinicId]);

  // "Order Medicines" only when the active clinic offers it (plan module + clinic switched on + pharmacy ready).
  const [hasMedicineOrders, setHasMedicineOrders] = useState(false);
  useEffect(() => {
    if (!patient) return;
    let cancelled = false;
    fetchPatientApi<{ offered: boolean }>('/api/patient/medicine-orders/availability')
      .then((res) => { if (!cancelled) setHasMedicineOrders(!!extractData<{ offered: boolean }>(res)?.offered); })
      .catch(() => { if (!cancelled) setHasMedicineOrders(false); });
    return () => { cancelled = true; };
  }, [patient, activeClinic?.clinicId]);

  useEffect(() => {
    if (!patient) return;
    let cancelled = false;
    const poll = async () => {
      try {
        // Lightweight: one COUNT query, not the full eligible-doctor list with a per-doctor unread loop.
        const res = await patientApi.messages.unreadCount();
        const total = extractData<{ count: number }>(res ?? {})?.count ?? 0;
        if (cancelled) return;
        const prev = prevUnreadRef.current;
        if (prev != null && total > prev && location.pathname !== '/patient/messages') {
          toast(i18n.t('messages:youHaveANewMessage'), { icon: '💬', id: 'patient-new-message' });
        }
        prevUnreadRef.current = total;
        setUnreadMessages(total);
      } catch { /* silent — badge just stays stale until the next successful poll */ }
    };
    // A short delay before the FIRST poll, rather than firing instantly on mount. This effect
    // re-runs (tearing down and restarting) every time activeClinic?.clinicId changes — correctly,
    // since unread count is per-clinic — but right after a fresh page load that itself just switched
    // clinics (e.g. the reload after booking at a new clinic via Find Care), that's exactly the
    // moment PatientTenantContext's own state is still settling from null to the real clinic. Firing
    // an immediate request into that window occasionally raced a genuinely stale/settling patient
    // session and 401'd — harmless (badge just stayed at its last value), but needless noise. 1.5s is
    // long enough for that settling to finish and short enough nobody would notice the badge isn't
    // instant on load.
    const initialTimer = setTimeout(poll, 1500);
    const stop = everyWhileVisible(poll, UNREAD_POLL_MS); // paused in a background tab
    return () => { cancelled = true; clearTimeout(initialTimer); stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.id, activeClinic?.clinicId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!patient) return <Navigate to="/patient/login" replace />;

  // The Agreement changed materially (or this account predates acceptance tracking existing at
  // all) — block every other portal screen until they've gone through it again. Declining signs
  // them out rather than leaving a half-authenticated session with nowhere else to go.
  if (patient.termsAcceptanceRequired) {
    return (
      <PatientTermsAndConditions
        onAccept={async () => {
          const res = await patientApi.auth.acceptCurrentTerms();
          if (!res.error) await refresh();
        }}
        onCancel={signOut}
      />
    );
  }

  const goTo = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
    if (path === '/patient/messages') setUnreadMessages(0);
  };

  const currentPage = (() => {
    for (const g of NAV_GROUPS) {
      for (const item of g.items) {
        if (location.pathname === item.path || item.alsoActiveOn?.includes(location.pathname)) {
          return { groupKey: g.titleKey, labelKey: item.labelKey };
        }
      }
    }
    return null;
  })();

  const badgeCount = (key?: NavItem['badgeKey']) => (key === 'messages' ? unreadMessages : key === 'checkins' ? pendingCheckins.length : key === 'forms' ? formsWaiting : 0);

  /**
   * Everything the removed top bar carried: brand, active clinic, dependent switching, messages,
   * profile and sign-out. The application-wide top bar is gone, so this lives at the top and bottom
   * of the sidebar instead.
   *
   * The "viewing X's records" safety banner is deliberately NOT here — it stays a full-width sticky
   * strip below, where a parent cannot miss whose chart they are on.
   */
  const SidebarChrome = ({ mini }: { mini: boolean }) => {
  const { t: tx } = useTranslation('portal');
  return (
    <>
      <Box sx={{ bgcolor: nd.nav, color: '#fff', px: mini ? 1 : 2, height: 64, display: 'flex', alignItems: 'center', justifyContent: mini ? 'center' : 'flex-start' }}>
        {!mini && (
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box component="img" src="/assets/images/clinvea-logo-dark.png?v=20261007" alt="ClinVea"
              sx={{ height: 30, width: 'auto', display: 'block' }} />
            <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.6rem', lineHeight: 1.6, textTransform: 'uppercase', letterSpacing: '0.06em' }} noWrap>
              {tx('patientPortal')}
            </Typography>
          </Box>
        )}
        {/* Same control as the staff/doctor/lab/pharmacy sidebar: a menu icon in the brand row. */}
        <Tooltip title={collapsed ? tx('expandSidebar') : tx('collapseSidebar')} placement="right">
          <IconButton onClick={toggleCollapsed} size="small" aria-label={collapsed ? tx('expandSidebar') : tx('collapseSidebar')}
            sx={{ color: 'inherit', flexShrink: 0 }}>
            <Menu size={mini ? 22 : 18} />
          </IconButton>
        </Tooltip>
      </Box>

    </>
  );
};

  const SidebarNav = ({ inDrawer = false }: { inDrawer?: boolean }) => {
  // Bound as `tx`: this file already uses `t` for something else.
  const { t: tx } = useTranslation('portal');
    // The mobile drawer always shows full labels — collapsing only applies to the persistent desktop rail.
    const mini = collapsed && !inDrawer;

    return (
      <Box sx={{ py: 1 }}>
        {NAV_GROUPS.map((group, gi) => (
          <Box key={group.titleKey} sx={{ mb: 0.5 }}>
            {mini ? (
              gi > 0 && <Divider sx={{ mx: 1.5, my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />
            ) : (
              <Typography
                variant="caption"
                sx={{ px: 2.5, pt: 1.5, pb: 0.5, display: 'block', fontWeight: 700, letterSpacing: '0.11em', color: nd.navLabel, textTransform: 'uppercase', fontSize: '0.655rem' }}
              >
                {tx(group.titleKey)}
              </Typography>
            )}
            <List sx={{ py: 0 }}>
              {group.items.filter((item) => (item.onlyIf !== 'hasPlans' || hasPlans) && (item.onlyIf !== 'homeCollection' || hasHomeCollection) && (item.onlyIf !== 'medicineOrders' || hasMedicineOrders) && (item.onlyIf !== 'insurance' || hasInsurance)).map(({ labelKey, path, icon: Icon, badgeKey, alsoActiveOn }) => {
                const label = tx(labelKey);
                const active = location.pathname === path || !!alsoActiveOn?.includes(location.pathname);
                const count = badgeCount(badgeKey);
                const button = (
                  <ListItemButton
                    key={path}
                    selected={active}
                    onClick={() => goTo(path)}
                    sx={{
                      borderRadius: 2, mx: 1.25, my: 0.25,
                      // Drawer rows are the primary mobile nav surface now, so they get a taller
                      // touch target (~44px) than the desktop rail's tighter, mouse-driven rows.
                      py: inDrawer ? 1.3 : 0.9,
                      justifyContent: mini ? 'center' : 'flex-start',
                      color: active ? '#fff' : nd.navText,
                      '& .MuiListItemIcon-root': { color: 'inherit' },
                      transition: 'background-color 0.15s ease, color 0.15s ease',
                      // Solid brand fill for the active route, as on the staff sidebar.
                      '&.Mui-selected': { bgcolor: nd.brand, color: '#fff' },
                      '&.Mui-selected:hover': { bgcolor: nd.brand, color: '#fff' },
                      '&:hover': { bgcolor: nd.navHover, color: '#fff' },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: mini ? 0 : 36, justifyContent: 'center', position: 'relative' }}>
                      <Icon size={18} />
                      {mini && count > 0 && (
                        <Box sx={{
                          position: 'absolute', top: -3, right: -3, width: 8, height: 8, borderRadius: '50%',
                          bgcolor: 'error.main', border: '2px solid', borderColor: nd.nav,
                        }} />
                      )}
                    </ListItemIcon>
                    {!mini && (
                      <>
                        <ListItemText primary={label} primaryTypographyProps={{ fontWeight: active ? 700 : 500, fontSize: '0.88rem' }} />
                        {count > 0 && (
                          <Chip
                            label={count > 9 ? '9+' : count}
                            size="small"
                            sx={{
                              height: 19, minWidth: 19, fontSize: '0.66rem', fontWeight: 700,
                              bgcolor: 'error.main',
                              color: '#fff', '& .MuiChip-label': { px: 0.6 },
                            }}
                          />
                        )}
                      </>
                    )}
                  </ListItemButton>
                );
                return mini ? (
                  <Tooltip key={path} title={label} placement="right">
                    {button}
                  </Tooltip>
                ) : button;
              })}
            </List>
          </Box>
        ))}
        {inDrawer && (
          <>
            <Divider sx={{ my: 1.5, mx: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
            <ListItemButton onClick={signOut} sx={{ borderRadius: 2, mx: 1.25 }}>
              <ListItemIcon sx={{ minWidth: 36 }}><LogOut size={18} /></ListItemIcon>
              <ListItemText primary={tx('signOut')} primaryTypographyProps={{ fontSize: '0.88rem', fontWeight: 500 }} />
            </ListItemButton>
            <Box sx={{ height: 8 }} />
          </>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{
      // overflowX 'clip', not 'hidden': 'hidden' makes this a scroll container and breaks the sticky sidebar/top bar.
      minHeight: '100vh', overflowX: 'clip',
      bgcolor: nd.bg,
    }}>
      {/* Gradient header — brand, active clinic, avatar. Page nav lives in the sidebar below. */}

      {/* Clinic and family switchers — triggered from the sidebar chrome above. The menus
          moved out of the removed AppBar; only their triggers relocated, not their contents. */}
      <MuiMenu anchorEl={clinicMenuAnchor} open={!!clinicMenuAnchor} onClose={() => setClinicMenuAnchor(null)}>
        <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary', fontWeight: 700 }}>
          {tx('yourClinics')}
        </Typography>
        {linkedClinics.map((c) => (
          <MenuItem
            key={c.clinicId}
            selected={c.clinicId === activeClinic?.clinicId}
            onClick={() => { setClinicMenuAnchor(null); switchClinic(c.clinicId); }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: 260 }}>
              <Avatar src={c.clinicLogoUrl} sx={{ width: 28, height: 28, bgcolor: 'primary.main' }}>
                <Building2 size={14} />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>{c.clinicName}</Typography>
                {c.isPrimary && <Chip label={tx('primary')} size="small" sx={{ height: 16, fontSize: '0.6rem' }} />}
              </Box>
              {c.clinicId === activeClinic?.clinicId && <Check size={16} />}
            </Stack>
          </MenuItem>
        ))}
      </MuiMenu>

      <MuiMenu anchorEl={familyMenuAnchor} open={!!familyMenuAnchor} onClose={() => setFamilyMenuAnchor(null)}>
        <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary', fontWeight: 700 }}>
          {tx('viewingRecordsFor')}
        </Typography>
        <MenuItem selected={!activeDependent} onClick={() => { setFamilyMenuAnchor(null); if (activeDependent) switchToSelf(); }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: 260 }}>
            <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main' }}>{initials(patient.name)}</Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>{patient.name}</Typography>
              <Chip label={tx('myself')} size="small" sx={{ height: 16, fontSize: '0.6rem' }} />
            </Box>
            {!activeDependent && <Check size={16} />}
          </Stack>
        </MenuItem>
        {activeDependents.map((d) => (
          <MenuItem
            key={d.dependentPatientId}
            selected={d.dependentPatientId === activeDependent?.dependentPatientId}
            onClick={() => { setFamilyMenuAnchor(null); switchToDependent(d.dependentPatientId); }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: 260 }}>
              <Avatar sx={{ width: 28, height: 28, bgcolor: 'secondary.main' }}>{initials(d.name)}</Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>{d.name}</Typography>
                <Chip label={enumLabel('familyRelationship', d.relationshipType)} size="small" sx={{ height: 16, fontSize: '0.6rem' }} />
              </Box>
              {d.dependentPatientId === activeDependent?.dependentPatientId && <Check size={16} />}
            </Stack>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={() => { setFamilyMenuAnchor(null); navigate('/patient/family'); }}>
          <ArrowLeftRight size={14} style={{ marginRight: 8 }} /> {tx('manageFamily')}
        </MenuItem>
      </MuiMenu>

      {/* Mobile top bar — below `md` this replaces the persistent icon rail entirely. The rail
          crammed 14 nav items into unlabeled 18px icons relying on hover tooltips, which don't
          work on a touch screen at all; the hamburger here opens the full labeled/grouped Drawer
          nav instead (previously built but never wired to any trigger, so it was unreachable). */}
      <Box sx={{
        display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1, height: 56, px: 1,
        position: 'sticky', top: 0, zIndex: (t) => t.zIndex.drawer + 1,
        bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider',
      }}>
        <IconButton onClick={() => setDrawerOpen(true)} aria-label={tx('menu')}>
          <Menu size={22} />
        </IconButton>
        <Box
          component="img"
          src="/assets/images/clinvea-icon-cropped.png?v=20261007"
          alt="ClinVea"
          sx={{ height: 24, width: 24, display: 'block', flexShrink: 0 }}
        />
        <Typography variant="subtitle2" fontWeight={800} sx={{ flex: 1, minWidth: 0 }} noWrap>
          {tx('patientPortal')}
        </Typography>
        <IconButton size="small" onClick={() => goTo('/patient/messages')} aria-label={tx('messages')} sx={{ color: 'text.secondary' }}>
          <Box sx={{ position: 'relative', display: 'flex' }}>
            <MessageCircle size={20} />
            {unreadMessages > 0 && (
              <Box sx={{
                position: 'absolute', top: -3, right: -3, width: 8, height: 8, borderRadius: '50%',
                bgcolor: '#ef4444', border: '2px solid', borderColor: 'background.paper',
              }} />
            )}
          </Box>
        </IconButton>
        <Avatar
          onClick={() => setDrawerOpen(true)}
          role="button"
          aria-label={tx('menu')}
          sx={{ width: 30, height: 30, bgcolor: 'primary.main', fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer' }}
        >
          {initials(patient.name)}
        </Avatar>
      </Box>

      {activeDependent && (
        <Box sx={{
          bgcolor: 'warning.light', color: 'warning.contrastText', px: 2, py: 0.75,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, flexWrap: 'wrap',
          // Sticks below the mobile top bar (56px) on xs/sm. On md+ it scrolls away; the desktop top bar
          // keeps a "Viewing <name>" chip in view instead.
          position: { xs: 'sticky', md: 'static' }, top: { xs: 56 }, zIndex: (t) => t.zIndex.drawer,
        }}>
          <Users size={14} />
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
  <Trans
    i18nKey="youAreViewingSRecords"
    ns="portal"
    values={{ name: activeDependent.name, toLowerCase: enumLabel('familyRelationship', activeDependent.relationshipType) }}
  />
</Typography>
          <Box
            component="button"
            onClick={switchToSelf}
            sx={{
              border: 'none', background: 'none', cursor: 'pointer', textDecoration: 'underline',
              fontWeight: 700, fontSize: 'inherit', color: 'inherit', px: 0.5,
            }}
          >
            {tx('switchBackToMyself')}
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        {/* Persistent sidebar — desktop (md+) only now. Below md, the mobile top bar + Drawer
            above is the actual nav; a permanent icon-only rail with 14 unlabeled items and no
            working way to reach the full labeled menu was the problem being fixed here. */}
        <Box
          component="nav"
          sx={{
            width: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH,
            flexShrink: 0, display: { xs: 'none', md: 'flex' }, flexDirection: 'column',
            position: 'sticky', top: 0, height: '100vh',
            bgcolor: nd.nav, color: nd.navText,
            transition: 'width 0.2s ease',
          }}
        >
          <SidebarChrome mini={railMode} />
          <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
            <SidebarNav />
          </Box>
        </Box>

        {/* Mobile drawer */}
        <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}
          // Above the sticky mobile top bar (drawer + 1), which otherwise covered the drawer header.
          sx={{ zIndex: (t) => t.zIndex.drawer + 2 }}
          PaperProps={{ sx: { bgcolor: nd.nav, color: nd.navText } }}>
          <Box sx={{ width: 272 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, fontSize: '0.85rem', fontWeight: 700 }}>
                  {initials(patient.name)}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#fff' }}>{patient.name}</Typography>
                  {patient.clinicName && (
                    <Typography variant="caption" sx={{ color: nd.navLabel }}>{patient.clinicName}</Typography>
                  )}
                </Box>
              </Stack>
              <IconButton size="small" onClick={() => setDrawerOpen(false)} sx={{ color: 'rgba(255,255,255,0.7)' }}><X size={18} /></IconButton>
            </Stack>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
            {isMultiClinic && (
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="caption" fontWeight={700} sx={{ color: nd.navLabel, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.655rem' }}>{tx('yourClinics')}</Typography>
                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                  {linkedClinics.map((c) => (
                    <Box
                      key={c.clinicId}
                      onClick={() => switchClinic(c.clinicId)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 2, cursor: 'pointer',
                        bgcolor: c.clinicId === activeClinic?.clinicId ? nd.navHover : 'transparent', '&:hover': { bgcolor: nd.navHover },
                      }}
                    >
                      <Avatar src={c.clinicLogoUrl} sx={{ width: 24, height: 24 }}><Building2 size={12} /></Avatar>
                      <Typography variant="body2" sx={{ flex: 1 }} noWrap>{c.clinicName}</Typography>
                      {c.clinicId === activeClinic?.clinicId && <Check size={14} />}
                    </Box>
                  ))}
                </Stack>
                <Divider sx={{ mt: 1.5, borderColor: 'rgba(255,255,255,0.1)' }} />
              </Box>
            )}
            {activeDependents.length > 0 && (
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="caption" fontWeight={700} sx={{ color: nd.navLabel, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.655rem' }}>{tx('viewingRecordsFor')}</Typography>
                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                  <Box
                    onClick={() => activeDependent && switchToSelf()}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 2, cursor: 'pointer',
                      bgcolor: !activeDependent ? nd.navHover : 'transparent', '&:hover': { bgcolor: nd.navHover },
                    }}
                  >
                    <Avatar sx={{ width: 24, height: 24 }}>{initials(patient.name)}</Avatar>
                    <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                      <Trans
                        i18nKey="myself2"
                        ns="portal"
                        values={{ name: patient.name }}
                      />
                    </Typography>
                    {!activeDependent && <Check size={14} />}
                  </Box>
                  {activeDependents.map((d) => (
                    <Box
                      key={d.dependentPatientId}
                      onClick={() => switchToDependent(d.dependentPatientId)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 2, cursor: 'pointer',
                        bgcolor: d.dependentPatientId === activeDependent?.dependentPatientId ? nd.navHover : 'transparent', '&:hover': { bgcolor: nd.navHover },
                      }}
                    >
                      <Avatar sx={{ width: 24, height: 24 }}>{initials(d.name)}</Avatar>
                      <Typography variant="body2" sx={{ flex: 1 }} noWrap>{d.name}</Typography>
                      {d.dependentPatientId === activeDependent?.dependentPatientId && <Check size={14} />}
                    </Box>
                  ))}
                </Stack>
                <Divider sx={{ mt: 1.5, borderColor: 'rgba(255,255,255,0.1)' }} />
              </Box>
            )}
            <SidebarNav inDrawer />
          </Box>
        </Drawer>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Desktop top bar (md+): sidebar toggle and page title on the left; messages, family switcher and the
              account menu on the right. These used to be squeezed into the bottom row of the sidebar. */}
          <Box component="header" sx={{
            display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1.5, height: 64, px: 3,
            position: 'sticky', top: 0, zIndex: (t) => t.zIndex.appBar,
            bgcolor: (th) => alpha(th.palette.background.paper, 0.85), backdropFilter: 'saturate(180%) blur(10px)',
            borderBottom: '1px solid', borderColor: 'divider',
          }}>
            {currentPage && (
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 600, lineHeight: 1.2 }} noWrap>
                  {tx(currentPage.groupKey)}
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }} noWrap>{tx(currentPage.labelKey)}</Typography>
              </Box>
            )}
            <Box sx={{ flex: 1 }} />

            {activeDependent && (
              <Chip size="small" color="warning" icon={<Users size={14} />} label={tx('viewing', { name: activeDependent.name })}
                onDelete={switchToSelf} sx={{ fontWeight: 700, maxWidth: 260 }} />
            )}

            {isMultiClinic && (
              <ButtonBase onClick={(e) => setClinicMenuAnchor(e.currentTarget)} aria-haspopup="menu"
                sx={{ gap: 0.75, px: 1.25, py: 0.75, borderRadius: 2, border: '1px solid', borderColor: 'divider', maxWidth: 240,
                  '&:hover': { bgcolor: 'action.hover' } }}>
                <Building2 size={16} />
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{activeClinic?.clinicName ?? patient.clinicName}</Typography>
                <ChevronDown size={14} style={{ opacity: 0.6, flexShrink: 0 }} />
              </ButtonBase>
            )}

            <Tooltip title={tx('messages')}>
              <IconButton onClick={() => goTo('/patient/messages')} aria-label={tx('messages')}
                sx={{ color: 'text.secondary', bgcolor: (th) => alpha(th.palette.primary.main, 0.06), '&:hover': { bgcolor: (th) => alpha(th.palette.primary.main, 0.12) } }}>
                <Badge badgeContent={unreadMessages} color="error" max={9} overlap="circular">
                  <MessageCircle size={19} />
                </Badge>
              </IconButton>
            </Tooltip>

            {activeDependents.length > 0 && (
              <Tooltip title={tx('switchFamilyMember')}>
                <IconButton onClick={(e) => setFamilyMenuAnchor(e.currentTarget)} aria-label={tx('switchFamilyMember')}
                  sx={{ color: 'text.secondary', bgcolor: (th) => alpha(th.palette.primary.main, 0.06), '&:hover': { bgcolor: (th) => alpha(th.palette.primary.main, 0.12) } }}>
                  <Users size={19} />
                </IconButton>
              </Tooltip>
            )}

            <Divider orientation="vertical" flexItem sx={{ my: 1.5 }} />

            <ButtonBase onClick={(e) => setAccountMenuAnchor(e.currentTarget)} aria-haspopup="menu"
              sx={{ borderRadius: 999, pl: 0.5, pr: 1.25, py: 0.5, gap: 1, '&:hover': { bgcolor: 'action.hover' } }}>
              <Avatar sx={{ width: 34, height: 34, fontSize: '0.8rem', fontWeight: 700, background: PORTAL_HEADER_GRADIENT }}>
                {initials(patient.name)}
              </Avatar>
              <Box sx={{ textAlign: 'left', minWidth: 0, maxWidth: 180 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>{patient.name}</Typography>
                {activeClinic?.clinicName && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }} noWrap>{activeClinic.clinicName}</Typography>
                )}
              </Box>
              <ChevronDown size={16} style={{ opacity: 0.6 }} />
            </ButtonBase>
          </Box>

          <MuiMenu anchorEl={accountMenuAnchor} open={!!accountMenuAnchor} onClose={() => setAccountMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { mt: 1, minWidth: 240, borderRadius: 3 } } }}>
            <ListItem sx={{ py: 1.25 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Avatar sx={{ width: 38, height: 38, fontSize: '0.85rem', fontWeight: 700, background: PORTAL_HEADER_GRADIENT }}>
                  {initials(patient.name)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{patient.name}</Typography>
                  {patient.email && <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{patient.email}</Typography>}
                </Box>
              </Stack>
            </ListItem>
            <Divider />
            <MenuItem onClick={() => { setAccountMenuAnchor(null); goTo('/patient/profile'); }}>
              <ListItemIcon><User size={16} /></ListItemIcon>{tx('profile')}
            </MenuItem>
            <MenuItem onClick={() => { setAccountMenuAnchor(null); goTo('/patient/family'); }}>
              <ListItemIcon><Users size={16} /></ListItemIcon>{tx('family')}
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { setAccountMenuAnchor(null); signOut(); }} sx={{ color: 'error.main' }}>
              <ListItemIcon sx={{ color: 'error.main' }}><LogOut size={16} /></ListItemIcon>{tx('signOut')}
            </MenuItem>
          </MuiMenu>

          <Box component="main" sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 }, py: { xs: 3, md: 3.5 }, overflowX: 'clip' }}>
            <Outlet />
            {/* Signed in while "Delete my account" is scheduled: offer to keep the account. */}
            <PendingDeletionDialog audience="patient" onSignOut={signOut} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
